/**
 * ProjectDetailPage — view project, manage members (admin), view tasks.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, UserMinus, UserPlus,
  Edit2, Save, X, Folder, CheckSquare,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTMAuth } from '@/contexts/TMAuthContext';
import {
  getProject, updateProject, deleteProject,
  addProjectMember, removeProjectMember,
  getApprovedTMUsers, getProjectTasks, updateTask,
} from '@/services/tmService';
import type { TMProject, TMUser, Task } from '@/types/tm.types';
import {
  PROJECT_COLORS, PROJECT_STATUS_LABELS,
  TASK_STATUS_LABELS, TASK_STATUS_DOT, TASK_STATUS_COLORS,
  TASK_PRIORITY_COLORS, TASK_PRIORITY_LABELS,
} from '@/types/tm.types';
import { cn } from '@/utils/cn';

const STATUSES = ['active', 'completed', 'archived'] as const;

function colorFor(key: string) {
  return PROJECT_COLORS.find(c => c.key === key) ?? PROJECT_COLORS[0];
}

function daysUntil(date: Date | null) {
  if (!date) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86400000);
}

export default function ProjectDetailPage() {
  const { id }            = useParams<{ id: string }>();
  const navigate          = useNavigate();
  const { tmUser, isTMAdmin } = useTMAuth();
  const canCreateTask = isTMAdmin || (tmUser?.permissions?.createTask ?? false);

  const [project,  setProject]  = useState<TMProject | null>(null);
  const [tasks,    setTasks]    = useState<Task[]>([]);
  const [allUsers, setAllUsers] = useState<TMUser[]>([]);
  const [loading,  setLoading]  = useState(true);

  // Edit state
  const [editing,     setEditing]     = useState(false);
  const [editName,    setEditName]    = useState('');
  const [editDesc,    setEditDesc]    = useState('');
  const [editColor,   setEditColor]   = useState('indigo');
  const [editStatus,  setEditStatus]  = useState<'active' | 'completed' | 'archived'>('active');
  const [saving,      setSaving]      = useState(false);

  // Member panel
  const [showAddMember, setShowAddMember] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      const [p, t] = await Promise.all([getProject(id), getProjectTasks(id)]);
      if (!p) { navigate('/tm/projects'); return; }
      setProject(p);
      setTasks(t);
      setEditName(p.name);
      setEditDesc(p.description ?? '');
      setEditColor(p.color);
      setEditStatus(p.status);
      if (isTMAdmin) {
        const users = await getApprovedTMUsers();
        setAllUsers(users);
      }
      setLoading(false);
    };
    load();
  }, [id, isTMAdmin]);

  const handleSave = async () => {
    if (!id || !project) return;
    setSaving(true);

    // If marking project as completed, move all non-done tasks to 'done'
    if (editStatus === 'completed' && project.status !== 'completed') {
      const incomplete = tasks.filter(t => t.status !== 'done');
      await Promise.all(incomplete.map(t => updateTask(t.id, { status: 'done', progress: 100 })));
      setTasks(prev => prev.map(t => t.status !== 'done' ? { ...t, status: 'done' as const, progress: 100 } : t));
    }

    await updateProject(id, {
      name:        editName.trim(),
      description: editDesc.trim() || null,
      color:       editColor,
      status:      editStatus,
    });
    setProject(p => p ? { ...p, name: editName.trim(), description: editDesc.trim() || null, color: editColor, status: editStatus } : p);
    toast.success('Project updated');
    setEditing(false);
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!id || !confirm(`Delete project "${project?.name}"? Tasks will remain but lose their project link.`)) return;
    await deleteProject(id);
    toast.success('Project deleted');
    navigate('/tm/projects');
  };

  const handleAddMember = async (uid: string) => {
    if (!id) return;
    await addProjectMember(id, uid);
    setProject(p => p ? { ...p, memberUids: [...p.memberUids, uid] } : p);
    toast.success('Member added');
  };

  const handleRemoveMember = async (uid: string) => {
    if (!id || !confirm('Remove this member from the project?')) return;
    await removeProjectMember(id, uid);
    setProject(p => p ? { ...p, memberUids: p.memberUids.filter(u => u !== uid) } : p);
    toast.success('Member removed');
  };

  if (loading || !project) {
    return <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Loading…</div>;
  }

  const color     = colorFor(project.color);
  const editColorDef = colorFor(editColor);

  const projectMembers = allUsers.filter(u => project.memberUids.includes(u.uid));
  const nonMembers     = allUsers.filter(u => !project.memberUids.includes(u.uid));

  const tasksDone    = tasks.filter(t => t.status === 'done').length;
  const tasksTotal   = tasks.length;
  const progressPct  = tasksTotal > 0 ? Math.round((tasksDone / tasksTotal) * 100) : 0;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        {isTMAdmin && (
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold active:scale-95 transition-all disabled:opacity-60">
                  <Save className="h-4 w-4" />{saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setEditing(false)}
                  className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                  <Edit2 className="h-4 w-4" /> Edit
                </button>
                <button onClick={handleDelete}
                  className="p-2 rounded-xl text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Project card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Color bar */}
        <div className={cn('h-1.5 w-full', editing ? editColorDef.bg : color.bg)} />
        <div className="p-5 space-y-4">

          {editing ? (
            <>
              <input
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className="w-full text-lg font-bold text-gray-900 px-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <textarea
                rows={2}
                value={editDesc}
                onChange={e => setEditDesc(e.target.value)}
                placeholder="Description…"
                className="w-full text-sm text-gray-600 px-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              />
              {/* Status */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Status</p>
                <div className="flex gap-2">
                  {STATUSES.map(s => (
                    <button key={s} type="button" onClick={() => setEditStatus(s)}
                      className={cn('px-3 py-1.5 rounded-full text-xs font-semibold border capitalize transition-all',
                        editStatus === s ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-200 text-gray-500')}>
                      {PROJECT_STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>
              {/* Color */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Colour</p>
                <div className="flex gap-2.5 flex-wrap">
                  {PROJECT_COLORS.map(c => (
                    <button key={c.key} type="button" onClick={() => setEditColor(c.key)}
                      className={cn('w-8 h-8 rounded-full relative transition-all', c.bg,
                        editColor === c.key && 'ring-2 ring-offset-2 ring-gray-400 scale-110')}>
                      {editColor === c.key && (
                        <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', color.light)}>
                  <Folder className={cn('h-5 w-5', color.text)} />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg font-bold text-gray-900">{project.name}</h1>
                  {project.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{project.description}</p>
                  )}
                </div>
                <span className={cn(
                  'shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full',
                  project.status === 'active'    ? 'bg-emerald-50 text-emerald-600' :
                  project.status === 'completed' ? 'bg-blue-50 text-blue-600' :
                                                    'bg-gray-100 text-gray-400',
                )}>
                  {PROJECT_STATUS_LABELS[project.status]}
                </span>
              </div>

              {/* Progress */}
              {tasksTotal > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs text-gray-400">{tasksDone} of {tasksTotal} tasks done</p>
                    <span className="text-xs font-bold text-gray-700">{progressPct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100">
                    <div className={cn('h-1.5 rounded-full transition-all', color.bg)} style={{ width: `${progressPct}%` }} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Members */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Members · {project.memberUids.length}
          </p>
          {isTMAdmin && nonMembers.length > 0 && (
            <button onClick={() => setShowAddMember(p => !p)}
              className={cn('flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-all',
                showAddMember ? 'bg-gray-100 text-gray-500' : 'bg-indigo-50 text-indigo-600')}>
              <UserPlus className="h-3.5 w-3.5" />
              {showAddMember ? 'Cancel' : 'Add'}
            </button>
          )}
        </div>

        {/* Current members */}
        {projectMembers.length === 0 ? (
          <p className="text-xs text-gray-400">No members added yet.</p>
        ) : (
          <div className="space-y-2">
            {projectMembers.map(m => (
              <div key={m.uid} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-indigo-600">{m.name.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{m.name}</p>
                  <p className="text-xs text-gray-400 truncate">{m.email}</p>
                </div>
                <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full capitalize',
                  m.role === 'admin' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500')}>
                  {m.role}
                </span>
                {isTMAdmin && (
                  <button onClick={() => handleRemoveMember(m.uid)}
                    className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <UserMinus className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add member picker */}
        {isTMAdmin && showAddMember && nonMembers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            <p className="text-xs text-gray-400 font-medium">Add from team:</p>
            {nonMembers.map(m => (
              <button key={m.uid} onClick={() => { handleAddMember(m.uid); setShowAddMember(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-50 hover:bg-indigo-50 transition-colors text-left">
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-gray-500">{m.name.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{m.name}</p>
                  <p className="text-xs text-gray-400 truncate">{m.email}</p>
                </div>
                <UserPlus className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tasks */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4 text-gray-400" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tasks · {tasksTotal}</p>
          </div>
          {canCreateTask && (
            <Link
              to={`/tm/tasks/new?projectId=${project.id}`}
              className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-600">
              <Plus className="h-3.5 w-3.5" /> Add task
            </Link>
          )}
        </div>

        {tasks.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-xs">
            No tasks in this project yet.
            {canCreateTask && (
              <div className="mt-2">
                <Link to={`/tm/tasks/new?projectId=${project.id}`}
                  className="text-indigo-500 font-semibold hover:underline">
                  Add the first task →
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map(task => {
              const d = daysUntil(task.dueDate);
              return (
                <Link key={task.id} to={`/tm/tasks/${task.id}`}
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <span className={cn('mt-1 w-2.5 h-2.5 rounded-full shrink-0', TASK_STATUS_DOT[task.status])} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{task.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-full', TASK_STATUS_COLORS[task.status])}>
                        {TASK_STATUS_LABELS[task.status]}
                      </span>
                      {task.assignedTo && (
                        <span className="text-[10px] text-gray-400">{task.assignedTo.name.split(' ')[0]}</span>
                      )}
                      {d !== null && (
                        <span className={cn('text-[10px] font-medium',
                          d < 0 ? 'text-red-500' : d <= 3 ? 'text-amber-600' : 'text-gray-400')}>
                          {d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Due today' : `${d}d left`}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={cn('shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full', TASK_PRIORITY_COLORS[task.priority])}>
                    {TASK_PRIORITY_LABELS[task.priority]}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
